import { isEmptyString, value } from '@takeshape/util';
import compose from 'lodash/fp/compose.js';
import set from 'lodash/fp/set.js';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
import { fixObfuscatedProp } from "../util/props.js";
const serviceProvider = {
    name: 'shipEngine',
    title: 'ShipEngine',
    description: 'Shipping API integration tools for FedEx, UPS, USPS & More',
    categories: ['ecommerce'],
    links: [{ title: 'Homepage', url: 'https://www.shipengine.com/' }],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('ship-engine'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'ShipEngine'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'ship-engine'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'ShipEngine'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    apiKey: {
                        title: 'API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['apiKey']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable name for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'A unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            authentication: {
                properties: {
                    apiKey: {
                        widget: 'sensitiveField',
                        instructions: 'API Key from Integrations > API'
                    }
                },
                order: ['apiKey']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            id: formData.id,
            title: formData.title,
            provider: 'shipEngine',
            serviceType: 'openapi',
            authenticationType: 'bearer',
            namespace: formData.namespace,
            options: {
                endpoint: 'https://api.shipengine.com/',
                spec: 'https://raw.githubusercontent.com/ShipEngine/shipengine-openapi/master/openapi.yaml',
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION,
                useRestfulNames: true
            }
        };
        if (!formData.authentication) {
            return config;
        }
        if (isEmptyString(formData.authentication.apiKey)) {
            config.authentication = null;
            return config;
        }
        const apiKey = fixObfuscatedProp(formData.authentication.apiKey);
        if (apiKey) {
            config.authentication = {
                type: 'bearer',
                header: 'API-Key',
                token: apiKey
            };
        }
        return config;
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.spec']),
    specTransform: (spec) => compose(set(['paths', '/v1/tracking', 'get', 'parameters', 0, 'required'], true), set(['paths', '/v1/tracking', 'get', 'parameters', 1, 'required'], true))(spec),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/v1/account/settings')
    })
};
export default serviceProvider;
